import {
  ModuleCategory,
  OptionalString,
  PublishesTo,
  SchemaTypeHelper,
  StringEnum,
  SubscribesTo,
  createComponentAdmin,
  sizingSchema,
} from '@backstage-components/base';
import {Static, Type} from '@sinclair/typebox';
import {ObjectFitModes} from './Image.types';

export const reactName = 'Image';
export const name = reactName;
export const description = reactName;
const category: ModuleCategory = 'media';

const defaultSkeletonColors = {
  startColor: 'rgba(0,0,0,0.01)',
  endColor: 'rgba(0,0,0,0.08)',
};

export const defaultTransitionDuration = '2';

const schema = Type.Object({
  src: Type.Object(
    {
      url: Type.String({title: 'Image'}),
    },
    {
      title: 'Source',
    }
  ),
  alt: OptionalString({title: 'Alt Text'}),
  aspectRatioImage: Type.Optional(
    Type.Object(
      {
        useAspectRatioImage: Type.Optional(
          Type.Boolean({
            title: 'Use Aspect Ratio Image',
            description:
              'Maintain the aspect ratio for your images and add loading and animation states',
            default: false,
          })
        ),
      },
      {
        title: 'Apect Ratio',
        dependencies: {
          useAspectRatioImage: {
            oneOf: [
              {
                properties: {
                  useAspectRatioImage: {
                    enum: [false],
                  },
                },
              },
              {
                properties: {
                  useAspectRatioImage: {
                    enum: [true],
                  },
                  ratio: Type.Optional(
                    Type.Object(
                      {
                        width: Type.Optional(
                          Type.Number({title: 'Intrinsic Width'})
                        ),
                        height: Type.Optional(
                          Type.Number({title: 'Instrinsic Height'})
                        ),
                      },
                      {
                        title: 'Aspect Ratio',
                        description:
                          'Maintain the aspect ratio of the image based off of the image width and height.',
                      }
                    )
                  ),
                  skeletonColor: Type.Optional(
                    Type.Object(
                      {
                        startColor: OptionalString('Start Color'),
                        endColor: OptionalString('End Color'),
                      },
                      {
                        title: 'Skeleton Colors',
                        description:
                          'Set colors for skeleton animation. Skeleton will fade in and out between the two chosen colors.',
                        default: defaultSkeletonColors,
                      }
                    )
                  ),
                  transitionDuration: OptionalString({
                    title: 'Duration',
                    description:
                      'The time it takes in seconds for the image to fade in when loaded.',
                    default: defaultTransitionDuration,
                  }),
                },
              },
            ],
          },
        },
      }
    )
  ),
  sizing: Type.Optional(
    Type.Intersect(
      [
        Type.Pick(sizingSchema, [
          'minHeight',
          'maxHeight',
          'minWidth',
          'maxWidth',
          'width',
          'height',
        ]),
      ],
      {
        title: 'Sizing',
        description:
          'When using aspect ratio, Min Height and Max Height are not applied. The image should fill the vertical space necessary to maintain aspect ratio. Use "Max Width" to contrain the images size.',
      }
    )
  ),
  objectFit: Type.Optional(
    StringEnum(ObjectFitModes, {title: 'Object Fit', default: 'none'})
  ),
  objectPosition: OptionalString({
    title: 'Object Position',
    default: '50% 50%',
  }),
});

const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              [
                'Image Properties',
                [
                  'src',
                  'alt',
                  'objectFit',
                  'objectPosition',
                  'aspectRatioImage',
                ],
              ],
              ['Sizing', ['sizing']],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [
              // prettier-ignore
              ['Custom Styles', ['styleAttr']],
            ],
          },
        ],
      ],
      [
        'Animations',
        [
          {
            'ui:template': 'accordion',
            sections: [['Animations', ['animationStates']]],
          },
        ],
      ],
    ],
  },
  src: {
    url: {
      'ui:widget': 'AssetSelectWidget',
      'ui:options': {
        buttonTitle: 'Select Asset',
      },
    },
  },
  aspectRatioImage: {
    skeletonColor: {
      startColor: {'ui:widget': 'color'},
      endColor: {'ui:widget': 'color'},
    },
  },
};

const defaultFieldData: Static<typeof schema> = {
  alt: 'Placeholder image',
  src: {
    url: 'https://s3.us-east-2.amazonaws.com/events.lcdigital.io/dlb_demo/defaults/default-image.jpg',
  },
  sizing: {maxWidth: '100%'},
  objectFit: 'none',
  objectPosition: '50% 50%',
  aspectRatioImage: {useAspectRatioImage: false},
};

const instructions = Type.Union([
  SubscribesTo({
    topic: `${reactName}:change`,
    description: 'Sets the source of an image',
    meta: {
      src: Type.String({
        title: 'Source',
        description: 'Source to which the image will be changed.',
      }),
    },
  }),
  SubscribesTo({
    topic: `${reactName}:click`,
    description: 'Clicks an image (virtually)',
  }),
  PublishesTo({
    topic: `${reactName}:on-load`,
    description: 'Image has loaded',
  }),
  PublishesTo({
    topic: `${reactName}:on-click`,
    description: 'Image was clicked',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: '100ccebb-71e0-4e38-924e-211898c41dcb',
  reactName,
  name,
  slug: reactName,
  description,
  version: 1,
  defaultFieldData,
  slotConfiguration: {},
  schema,
  uiSchema,
  instructions,
  category,
})
  .withAnimationStates()
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
